"use client";

import { useLang } from "@/contexts/language-context";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, MessageSquare, Sparkles } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { CASH_DISCOUNT_PCT, cardToCash } from "@/lib/utils/pricing";

type StepKey = "idle" | "tap1" | "tap2" | "tap3" | "tap4" | "card" | "sms";

const ITEMS = [
  { name: "Birria Taco", price: 4.5 },
  { name: "Birria Taco", price: 4.5 },
  { name: "Horchata", price: 3.0 },
  { name: "Chips & Salsa", price: 2.5 },
];

const STEP_DELAY = 700;

export default function FoodTruckQsrHero() {
  const { t } = useLang();
  const [step, setStep] = useState<StepKey>("idle");
  const [showSms, setShowSms] = useState(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const sequence: StepKey[] = ["tap1", "tap2", "tap3", "tap4", "card", "sms"];
    const start = () => {
      setStep("idle");
      setShowSms(false);
      sequence.forEach((s, i) => {
        timers.push(
          setTimeout(() => {
            setStep(s);
            if (s === "sms") setShowSms(true);
          }, STEP_DELAY * (i + 1))
        );
      });
      timers.push(setTimeout(start, STEP_DELAY * (sequence.length + 4)));
    };
    start();
    return () => timers.forEach(clearTimeout);
  }, []);

  const tapsVisible =
    step === "tap1"
      ? 1
      : step === "tap2"
      ? 2
      : step === "tap3"
      ? 3
      : step === "tap4" || step === "card" || step === "sms"
      ? 4
      : 0;

  const subtotal = ITEMS.slice(0, tapsVisible).reduce((a, b) => a + b.price, 0);
  // Dual pricing: menu prices are the card price; cash gets the discount (default rate, mock only).
  const cashPrice = cardToCash(subtotal, CASH_DISCOUNT_PCT);
  const total = subtotal;

  return (
    <section className="relative pt-28 pb-24 px-6 overflow-hidden bg-[#FAFAFA]">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/solution-food-trucks.mp4" type="video/mp4" />
      </video>
      {/* Light overlay — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAFA]/80 pointer-events-none" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-redy-orange/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[450px] h-[450px] bg-redy-red/6 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-white border border-black/5 px-4 py-2 rounded-full text-xs font-bold text-redy-charcoal shadow-sm">
            <Sparkles className="w-3 h-3 text-redy-orange fill-redy-orange" />
            {t("Food Trucks & Quick-Service", "Food Trucks y Quick-Service")}
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center text-[44px] sm:text-5xl md:text-[72px] font-black leading-[1.02] tracking-tight mb-6 max-w-[900px] mx-auto"
        >
          <span className="text-redy-charcoal">{t("Counter to car.", "Mostrador al carro.")}</span>
          <br />
          <span className="redy-text-gradient">{t("In 90 seconds.", "En 90 segundos.")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center text-lg md:text-xl text-redy-secondary max-w-[620px] mx-auto leading-relaxed mb-10 font-medium"
        >
          {t(
            "Counter-service POS for food trucks, taquerías, and QSR. Dual pricing so card payers cover the processing cost, offline cash mode for festivals, SMS pickup alerts so customers come back.",
            "POS de mostrador para food trucks, taquerías y QSR. Dual pricing para que quien paga con tarjeta cubra el procesamiento, modo efectivo offline para festivales, alertas SMS para que el cliente regrese."
          )}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-3 justify-center mb-16"
        >
          <Link
            href="/auth/sign-up"
            className="inline-flex items-center justify-center gap-2 bg-redy-charcoal hover:bg-black text-white px-7 py-4 rounded-full font-bold text-sm transition-all hover:scale-[1.02]"
          >
            {t("Start free →", "Empieza gratis →")}
          </Link>
          <Link
            href="/pricing"
            className="inline-flex items-center justify-center gap-2 bg-white border border-black/10 hover:border-black/30 text-redy-charcoal px-7 py-4 rounded-full font-bold text-sm transition-all"
          >
            {t("See pricing", "Ver precios")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        {/* The counter-rush mockup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative mx-auto max-w-[880px]"
        >
          <div className="relative bg-white border border-black/[0.06] rounded-[28px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.18)] p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-6 items-stretch">
              {/* iPad POS */}
              <div className="relative">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-redy-charcoal/50 mb-3">
                  {t("iPad — Counter POS", "iPad — POS de mostrador")}
                </div>
                <div className="bg-redy-charcoal rounded-[20px] p-3">
                  <div className="bg-[#FDFCFA] rounded-[14px] p-4 min-h-[280px] flex flex-col">
                    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/60 mb-3">
                      <span>{t("Order #042", "Orden #042")}</span>
                      <span>11:47 AM</span>
                    </div>
                    <div className="flex-1 space-y-2">
                      {ITEMS.map((it, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{
                            opacity: i < tapsVisible ? 1 : 0,
                            x: i < tapsVisible ? 0 : -8,
                          }}
                          transition={{ duration: 0.25 }}
                          className="flex items-center justify-between text-sm text-redy-charcoal"
                        >
                          <span className="font-medium">{it.name}</span>
                          <span className="font-mono tabular-nums">${it.price.toFixed(2)}</span>
                        </motion.div>
                      ))}
                    </div>
                    <div className="border-t border-black/5 mt-3 pt-3 space-y-1 text-sm">
                      <div className="flex justify-between text-redy-charcoal/60">
                        <span>{t("Subtotal", "Subtotal")}</span>
                        <span className="font-mono tabular-nums">${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-redy-orange">
                        <span className="font-semibold">
                          {t(
                            `Pay cash: ${Math.round(CASH_DISCOUNT_PCT * 100)}% off`,
                            `En efectivo: ${Math.round(CASH_DISCOUNT_PCT * 100)}% menos`
                          )}
                        </span>
                        <span className="font-mono tabular-nums font-semibold">${cashPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-redy-charcoal font-bold text-base pt-1">
                        <span>{t("Total", "Total")}</span>
                        <motion.span
                          key={total}
                          initial={{ scale: 0.9 }}
                          animate={{ scale: 1 }}
                          className="font-mono tabular-nums"
                        >
                          ${total.toFixed(2)}
                        </motion.span>
                      </div>
                    </div>
                    <AnimatePresence>
                      {(step === "card" || step === "sms") && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          className="mt-3 bg-redy-charcoal text-white rounded-lg px-3 py-2 text-xs font-bold flex items-center justify-between"
                        >
                          <span>{t("Tap card on terminal", "Acerca tarjeta al terminal")}</span>
                          <span className="text-redy-orange">●●●</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Right column: terminal + SMS */}
              <div className="flex flex-col gap-4">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-redy-charcoal/50 mb-3">
                    {t("North Terminal", "Terminal North")}
                  </div>
                  <div className="bg-redy-charcoal rounded-[18px] p-5 flex flex-col items-center justify-center min-h-[150px]">
                    <motion.div
                      animate={{
                        scale: step === "card" ? [1, 1.06, 1] : 1,
                      }}
                      transition={{ duration: 0.8, repeat: step === "card" ? Infinity : 0 }}
                      className="text-white font-mono tabular-nums text-2xl font-black"
                    >
                      ${total.toFixed(2)}
                    </motion.div>
                    <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-white/60">
                      {step === "sms"
                        ? t("Approved ✓", "Aprobado ✓")
                        : step === "card"
                        ? t("Awaiting tap…", "Esperando tap…")
                        : t("Ready", "Listo")}
                    </div>
                  </div>
                </div>

                <div className="relative flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-redy-charcoal/50 mb-3">
                    {t("SMS to customer", "SMS al cliente")}
                  </div>
                  <div className="bg-[#F1F0EC] rounded-[18px] p-4 min-h-[130px] flex items-center justify-center">
                    <AnimatePresence>
                      {showSms && (
                        <motion.div
                          initial={{ opacity: 0, y: 12, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.4 }}
                          className="bg-white rounded-2xl px-4 py-3 shadow-sm max-w-[260px]"
                        >
                          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50 mb-1">
                            <MessageSquare className="w-3 h-3" />
                            <span>Redy</span>
                          </div>
                          <p className="text-sm text-redy-charcoal leading-snug">
                            {t(
                              "Tu birria está lista #042 🌮 Pásala a recoger.",
                              "Your birria is ready #042 🌮 Come pick it up."
                            )}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {!showSms && (
                      <div className="text-xs text-redy-charcoal/40 font-medium">
                        {t("Sends on approval", "Se envía al aprobar")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Caption */}
            <div className="mt-6 pt-5 border-t border-black/5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-redy-charcoal/60 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5 text-redy-orange" />
                {t("4 taps · 1 tap to pay · 1 SMS sent", "4 taps · 1 tap para pagar · 1 SMS enviado")}
              </div>
              <div className="font-bold text-redy-charcoal tabular-nums">
                {step === "sms" ? "00:42" : step === "card" ? "00:30" : "—"}
                <span className="text-redy-charcoal/40 font-normal ml-1">
                  {t("from tap to ready", "tap a listo")}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
